<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { Headphones, Users, Compass, Zap } from '@lucide/svelte';

  const whatYouGet = [
    {
      title: 'Priority support',
      description: 'Direct line to the engineering team. Issues get triaged immediately. Feature requests get heard first.',
      icon: Headphones
    },
    {
      title: 'Direct access to the founding team',
      description: 'Regular working sessions with the people building the platform. No account management layer in between.',
      icon: Users
    },
    {
      title: 'Influence over the roadmap',
      description: 'Your use cases shape what we build next. Your feedback becomes product decisions.',
      icon: Compass
    },
    {
      title: 'Early access to everything',
      description: 'New capabilities land in your tenant before they ship broadly. Help us harden them in production environments that matter.',
      icon: Zap
    }
  ];

  const whoItsFor = [
    'Enterprises with real AI workloads — not experiments, not prototypes',
    'Teams that have tried to build this themselves and know the edges',
    'Organizations with data residency or compliance constraints that shape their architecture',
    'Product companies and SIs building AI offerings on a foundation they don\'t want to maintain',
  ];
</script>

<PageMeta
  title="Design Partner Program — Shape the platform from day one"
  description="Early adopters working closely with the Corral team. Priority support, direct access to the founding team, and influence over the roadmap."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <p class="font-mono text-[14px] text-[#ee6018] mb-4">Design Partner Program</p>
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Shape the platform from day one.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          We work closely with a small group of early adopters to shape how Corral evolves. Priority support, direct access to the founding team, and real influence over the roadmap. If you're serious about deploying AI inside your company and want to help define what the platform becomes — this is for you.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: What you get — 2x2 grid -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="mb-12">
        <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-3 block">What you get</span>
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1]">
          More than a support contract.
        </h2>
      </div>

      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 md:grid-cols-2 gap-0">
          {#each whatYouGet as item, i}
            <div class="bg-[#171719] border border-[#333335] p-6 lg:p-8 {i % 2 !== 0 ? 'lg:-ml-px' : ''} {i >= 2 ? '-mt-px' : ''}">
              <CornerFrame
                class="w-[43px] h-[44px] flex items-center justify-center mb-4"
                bg="bg-transparent"
                borderColor="rgba(255, 255, 255, 0.3)"
                cornerColor="white"
                cornerSize={7}
              >
                <svelte:component this={item.icon} class="w-4 h-4 text-white" />
              </CornerFrame>
              <h3 class="font-sans text-[18px] text-white mb-3">{item.title}</h3>
              <p class="font-mono font-light text-[14px] text-[#878787] leading-relaxed">{item.description}</p>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §2: Who it's for -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-3 block">Who it's for</span>
        <h2 class="font-sans text-[36px] lg:text-[48px] text-[#020202] tracking-[-0.04em] leading-[1.1] mb-8">
          Serious people shipping AI inside companies.
        </h2>
        <p class="font-body font-light text-[16px] text-[#5C5855] leading-relaxed mb-8">
          This isn't a marketing program. We're looking for a small number of partners who will actively use the platform, push its edges, and give us real feedback. In exchange, you get the platform shaped around your workflows.
        </p>
        <ul class="flex flex-col gap-4">
          {#each whoItsFor as item}
            <li class="flex items-start gap-3">
              <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
              <span class="font-mono font-light text-[15px] text-[#020202] leading-relaxed">{item}</span>
            </li>
          {/each}
        </ul>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §3: CTA -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto text-center">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-6">
          Ready to apply?
        </h2>
        <p class="font-mono font-light text-[16px] text-[#878787] leading-relaxed mb-10">
          Tell us about your use case, your team, and what you're trying to build. We'll respond within 48 hours.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/company/contact?subject=Design+Partner+Program"
            class="bg-white text-black font-mono text-[14px] px-6 py-3 hover:bg-gray-100 transition-colors"
          >
            Apply to join
          </a>
          <a
            href="/why-corral"
            class="border border-[#333335] text-white font-mono text-[14px] px-6 py-3 hover:border-[#ee6018]/40 transition-colors"
          >
            Read the thesis first
          </a>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>
